import { useState, useEffect } from "react";
import "../Leads.css";
import { Undo2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { mapLeadData, propertyTypeLabels } from "../../../utils/common";
import { NEARBY_RADIUS as columns } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";
import UndoLeadModal from "../../../components/UndoModal";
import { useAuth } from "../../../context/AuthProvider";
import RangeInputs from "../../../components/RangeInputs";

const FlippedProperties = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    propertiesInRadius: user.radius || 10,
    lastSoldMonths: user.last_sold_months || 12,
    lastPurchaseMonths: user.last_purchase_months || 12,
    beds: [null, null],
    baths: [null, null],
    propertyType: "",
  });
  const [fetchLoading, setFetchLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  const [selectedRow, setSelectedRow] = useState(null);
  const [undoModal, setUndoModal] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const [leads, setLeads] = useState([]);

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  const totalPages = Math.ceil((totalLeads || 0) / leadsPerPage);

  const fetchLeads = async (page = 1, limit = leadsPerPage, showLoader = true) => {
    try {
      if (showLoader) setFetchLoading(true);

      const newParams = new URLSearchParams(searchParams.toString());
      newParams.set("page", page);
      setSearchParams(newParams);

      const offset = (page - 1) * limit;

      const resp = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/dealmachine_properties/leads`, {
        params: {
          ai_data: true,
          limit,
          offset,
          owner: false,
          sortBy: "updated_at",
          sortOrder: "DESC",
          status: "contact_saved",
          propertiesInRadius: formData.propertiesInRadius,
          lastSoldMonths: formData.lastSoldMonths,
          lastPurchaseMonths: formData.lastPurchaseMonths,
          bedsMin: formData.beds?.[0] ?? null,
          bedsMax: formData.beds?.[1] ?? null,

          bathsMin: formData.baths?.[0] ?? null,
          bathsMax: formData.baths?.[1] ?? null,

          propertyType: formData.propertyType || null,
        },
      });

      const data = await resp.data;

      if (data.success) {
        setLeads(data.leads || []);
        setTotalLeads(data.total);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Failed to fetch leads", err);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchLeads(currentPage, leadsPerPage, false);
    }, 60000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, [currentPage, formData]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1, leadsPerPage, true); // eslint-disable-next-line
  }, [formData]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [currentPage]);

  const totalTableWidth = columns.reduce((sum, col) => sum + col.width, 0);

  const handleUndo = async (id, complete = false) => {
    try {
      const res = await axiosInstance.delete(`api/dealmachine_properties/lead/${id}?complete=${complete}`);

      if (res.data.success) {
        console.log("Lead undo successful:", res.data);
        fetchLeads();
      } else {
        console.warn("Lead undo failed:", res.data.message);
      }
    } catch (error) {
      console.error("Error undo lead:", error.message || error);
    }
  };

  const radiusInMiles = {
    1: "1 Mile",
    2: "2 Miles",
    3: "3 Miles",
    4: "4 Miles",
    5: "5 Miles",
    6: "6 Miles",
    7: "7 Miles",
    8: "8 Miles",
    9: "9 Miles",
    10: "10 Miles",
    15: "15 Miles",
    20: "20 Miles",
    25: "25 Miles",
    30: "30 Miles",
  };

  const lastSoldMonths = {
    1: "1 Month",
    2: "2 Months",
    3: "3 Months",
    4: "4 Months",
    5: "5 Months",
    6: "6 Months",
    7: "7 Months",
    8: "8 Months",
    9: "9 Months",
    10: "10 Months",
    11: "11 Months",
    12: "1 Year",
    24: "2 Years",
  };

  const lastPurchaseMonths = {
    1: "1 Month",
    2: "2 Months",
    3: "3 Months",
    4: "4 Months",
    5: "5 Months",
    6: "6 Months",
    7: "7 Months",
    8: "8 Months",
    9: "9 Months",
    10: "10 Months",
    11: "11 Months",
    12: "1 Year",
    24: "2 Years",
  };

  return (
    <>
      <div>
        <div className="input_fields_container">
          <form className="w-full grid sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="input_field">
              <span className="text-slate-500/90 whitespace-nowrap min-w-32 text-left w-max-content"> Radius: </span>
              <select name="propertiesInRadius" className={`cursor-pointer w-full focus:!outline-none ${formData.propertiesInRadius ? "text-gray-700" : "text-gray-500"}`} value={formData.propertiesInRadius || ""} onChange={(e) => handleChange("propertiesInRadius", e.target.value)}>
                {Object.entries(radiusInMiles).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="input_field">
              <span className="text-slate-500/90 whitespace-nowrap min-w-32 text-left w-max-content"> Last Sold: </span>
              <select name="lastSoldMonths" className={`cursor-pointer w-full focus:!outline-none ${formData.lastSoldMonths ? "text-gray-700" : "text-gray-500"}`} value={formData.lastSoldMonths || ""} onChange={(e) => handleChange("lastSoldMonths", e.target.value)}>
                {Object.entries(lastSoldMonths).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="input_field">
              <span className="text-slate-500/90 whitespace-nowrap min-w-32 text-left w-max-content"> Last Purchase: </span>
              <select name="lastPurchaseMonths" className={`cursor-pointer w-full focus:!outline-none ${formData.lastPurchaseMonths ? "text-gray-700" : "text-gray-500"}`} value={formData.lastPurchaseMonths || ""} onChange={(e) => handleChange("lastPurchaseMonths", e.target.value)}>
                {Object.entries(lastPurchaseMonths).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <RangeInputs label="Beds" value={formData.beds} onChange={(val) => handleChange("beds", val)} />
            <RangeInputs label="Baths" value={formData.baths} onChange={(val) => handleChange("baths", val)} />

            <div className="input_field">
              <select name="propertyType" className={`cursor-pointer w-full focus:!outline-none ${formData.propertyType ? "text-gray-700" : "text-gray-500"}`} value={formData.propertyType || ""} onChange={(e) => handleChange("propertyType", e.target.value)}>
                <option value="">-- Select Property Type --</option>
                {Object.entries(propertyTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>

        <div className="mt-6 !w-full !overflow-x-auto">
          <table className="!border !border-gray-300 !table-auto min-w-[98%]" style={{ width: `${totalTableWidth}px` }}>
            <thead>
              <tr className="!bg-gray-200">
                <th className="!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap !w-8 !min-w-8"></th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`!border !border-gray-300 !px-4 !py-2`}
                    style={{
                      minWidth: `${col.width}px`,
                      width: `${col.width}px`,
                      textAlign: col.headerAlign,
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!fetchLoading ? (
                <>
                  {leads.length > 0 ? (
                    leads.map((lead, idx) => {
                      const row = mapLeadData(lead);
                      return (
                        <tr key={lead.id || idx} className="!hover:bg-gray-100">
                          <td className="!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap !w-4">
                            <button
                              onClick={() => {
                                setUndoModal(true);
                                setSelectedRow(lead);
                              }}
                              className="!text-red-600 hover:!text-red-800 !flex !items-center !justify-center !px-1 !py-2 cursor-pointer"
                              title="Undo Lead"
                            >
                              <Undo2 size={18} />
                            </button>
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col.key}
                              className={`truncate !border !border-gray-300 !px-4 !py-2 text-sm
                            ${row[col.key] === "Not Found" ? "text-red-500" : /(CONFIRMED)/i.test((row[col.key] ?? "").toString()) ? "text-green-500" : /(NO-ANSWER)/i.test((row[col.key] ?? "").toString()) ? "text-yellow-500" : "text-black"}`}
                              style={{
                                minWidth: `${col.width}px`,
                                width: `${col.width}px`,
                                textAlign: col.cellAlign,
                                whiteSpace: "pre-line",
                              }}
                            >
                              {row[col.key]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                        <div className="w-full max-w-screen text-center">No leads found.</div>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <>
                  <tr>
                    <td colSpan={columns.length + 1} className="!border !border-gray-300 !p-4 !text-left !text-gray-500">
                      <div className="w-full max-w-screen text-center">
                        <LoadingSpinner size={30} textSize={20} />
                      </div>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col items-center !mb-10">
          <span className="text-sm text-gray-700">
            Showing <span className="font-semibold text-gray-900">{indexOfFirstLead + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastLead, totalLeads || 0)}</span> of <span className="font-semibold text-gray-900">{totalLeads || 0}</span> Entries
          </span>

          {/* Pagination */}
          <div className="inline-flex !mt-2 xs:mt-0">
            {/* Prev Button */}
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => fetchLeads(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </button>

            {/* First Page */}
            <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(1)} disabled={currentPage === 1}>
              1
            </button>

            {/* Ellipses before middle pages */}
            {currentPage > 3 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

            {/* Middle Pages */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => page !== 1 && page !== totalPages && page >= currentPage - 1 && page <= currentPage + 1)
              .map((page) => (
                <button key={page} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === page ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(page)}>
                  {page}
                </button>
              ))}

            {/* Ellipses after middle pages */}
            {currentPage < totalPages - 2 && totalPages > 5 && <span className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">...</span>}

            {/* Last Page */}
            {totalPages > 1 && (
              <button className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => fetchLeads(totalPages)} disabled={currentPage === totalPages}>
                {totalPages}
              </button>
            )}

            {/* Next Button */}
            <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => fetchLeads(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </div>

        <UndoLeadModal
          isOpen={undoModal}
          title="Undo Lead"
          message="Do you want to undo this lead completely or only in Deal Machine?"
          onCancel={() => {
            setUndoModal(false);
            setSelectedRow(null);
          }}
          onCompleteUndo={() => handleUndo(selectedRow.id, true)}
          onDealMachineUndo={() => handleUndo(selectedRow.id, false)}
          buttonColor="red"
        />
      </div>
    </>
  );
};

export default FlippedProperties;
